import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { AlertTriangle, CircleStop, Clock3, Database, Diamond, Flag, GitFork, Play, Repeat2, UserRoundCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ProcessDataCoverageState, ProcessNodeDefinition } from '@/frontend/features/Fde/types'

const NODE_STYLE = {
  trigger: { icon: Play, accent: 'bg-emerald-500' },
  activity: { icon: Flag, accent: 'bg-violet-500' },
  decision: { icon: Diamond, accent: 'bg-amber-500' },
  wait: { icon: Clock3, accent: 'bg-blue-500' },
  handoff: { icon: UserRoundCheck, accent: 'bg-cyan-500' },
  event: { icon: Repeat2, accent: 'bg-sky-500' },
  subprocess: { icon: GitFork, accent: 'bg-indigo-500' },
  end: { icon: CircleStop, accent: 'bg-slate-700' },
  gap: { icon: AlertTriangle, accent: 'bg-rose-500' },
} as const

export interface ProcessFlowNodeData extends Record<string, unknown> {
  definition: ProcessNodeDefinition
  active: boolean
  kindLabel: string
  connectionsLabel: string
  evidencesLabel: string
  context: string[]
  dataCoverage: { state: ProcessDataCoverageState; mapped: number; total: number } | null
  dataCoverageLabel: string
}

export type ProcessCanvasNode = Node<ProcessFlowNodeData, 'process'>

export default function ProcessFlowNode({ data }: NodeProps<ProcessCanvasNode>) {
  const style = NODE_STYLE[data.definition.kind]
  const Icon = style.icon
  return (
    <div className={cn(
      'relative flex size-full overflow-hidden rounded-xl border bg-card shadow-sm transition-[border-color,box-shadow]',
      data.definition.kind === 'gap' && 'border-dashed',
      data.active ? 'border-primary ring-4 ring-primary/10' : 'border-border',
    )} data-testid="process-flow-node" data-process-node-id={data.definition.id}>
      <Handle type="target" position={Position.Left} className="!size-2 !border-2 !border-background !bg-muted-foreground" />
      <span className={cn('w-1 shrink-0', style.accent)} />
      <div className="min-w-0 flex-1 px-3 py-2.5">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon className="size-3" /><span>{data.kindLabel}</span>
        </div>
        <p className="mt-1.5 line-clamp-2 text-xs font-semibold leading-4 text-foreground">{data.definition.label}</p>
        {data.context.length > 0 && <p className="mt-1.5 line-clamp-2 text-[10px] leading-3.5 text-muted-foreground">{data.context.join(' · ')}</p>}
        <div className="mt-1.5 flex items-center gap-2 text-[9px] text-muted-foreground">
          {data.definition.ontology_refs.length > 0 && <span>{data.definition.ontology_refs.length} {data.connectionsLabel}</span>}
          {data.definition.evidence_ids.length > 0 && <span>{data.definition.evidence_ids.length} {data.evidencesLabel}</span>}
          {data.dataCoverage && <span className={cn('ml-auto flex items-center gap-1 font-medium', data.dataCoverage.state === 'missing' ? 'text-rose-600' : data.dataCoverage.state === 'confirmed' ? 'text-emerald-700' : 'text-amber-700')}><Database className="size-2.5" />{data.dataCoverage.mapped}/{data.dataCoverage.total} {data.dataCoverageLabel}</span>}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!size-2 !border-2 !border-background !bg-muted-foreground" />
    </div>
  )
}
